import { Check, AlertTriangle, Zap, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import UsageHint from "./UsageHint";
import {
  fmtPrice,
  type PurchaseResult,
  type PurchaseError,
} from "./creditsConfig";

interface Props {
  result: PurchaseResult | null;
  error: PurchaseError | null;
  onBackToChat: () => void;
  onRetry: () => void;
}

export default function SuccessView({
  result,
  error,
  onBackToChat,
  onRetry,
}: Props) {
  const { t } = useTranslation();

  if (error) {
    return (
      <div className="flex min-h-full items-center justify-center px-5 py-8">
        <div className="w-full max-w-[460px] rounded-[14px] border border-border bg-card px-6 py-7 text-center">
          <div
            className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full"
            style={{
              background: "hsl(0 84% 60% / 0.15)",
              color: "hsl(0 84% 65%)",
            }}
          >
            <AlertTriangle size={22} />
          </div>
          <h2 className="mb-2 text-[22px] font-semibold tracking-tight">
            {t("payments.success.loadError")}
          </h2>
          <p className="mb-4 text-[14px] leading-relaxed text-muted-foreground">
            {t("payments.success.loadErrorDesc")}
          </p>
          <div className="font-mono mb-5 break-all rounded-lg border border-border bg-muted/50 px-2.5 py-2 text-[11.5px] text-muted-foreground">
            session_id={error.sessionId || "(none)"}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onRetry}
              className="h-11 flex-1 text-sm font-semibold"
            >
              {t("payments.success.tryAgain")}
            </Button>
            <Button
              onClick={onBackToChat}
              className="h-11 flex-1 text-sm font-semibold"
            >
              {t("payments.success.backToChat")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }
  const { amount, dollars, newBalance } = result;

  return (
    <div className="flex min-h-full items-center justify-center px-5 py-8">
      <div className="w-full max-w-[460px] rounded-[14px] border border-border bg-card px-6 py-7">
        <div className="mb-5 text-center">
          <div
            className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full"
            style={{
              background: "hsl(142 71% 45% / 0.15)",
              color: "hsl(142 65% 55%)",
            }}
          >
            <Check size={22} />
          </div>
          <p
            className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em]"
            style={{ color: "hsl(142 65% 55%)" }}
          >
            {t("payments.success.purchaseConfirmed")}
          </p>
          <h2 className="text-[18px] font-semibold tracking-tight">
            {t("payments.success.youPurchased", {
              amount: amount.toLocaleString(),
            })}
          </h2>
        </div>

        <div className="mb-3.5 divide-y divide-dashed divide-border">
          <div className="flex items-baseline justify-between py-3 text-[14px]">
            <span>{t("payments.success.addonCredits")}</span>
            <strong className="font-semibold tabular-nums">
              +{amount.toLocaleString()}
            </strong>
          </div>
          <div className="flex items-baseline justify-between py-3 text-[14px]">
            <span>{t("payments.success.charged")}</span>
            <span className="tabular-nums text-muted-foreground">
              {fmtPrice(dollars)}
            </span>
          </div>
          <div className="flex items-baseline justify-between pt-3.5 text-[14px]">
            <span>{t("payments.success.newBalance")}</span>
            <strong className="text-[18px] font-semibold tabular-nums">
              {newBalance.toLocaleString()} {t("payments.balance.credits")}
            </strong>
          </div>
        </div>

        <div className="mb-3 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Zap size={12} className="shrink-0" />
          <span>{t("payments.success.creditsDelay")}</span>
        </div>

        <div className="mb-4">
          <UsageHint />
        </div>

        <Button
          onClick={onBackToChat}
          className="h-11 w-full gap-2 text-[14px] font-semibold"
        >
          {t("payments.success.backToChat")}
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}
